import { IonAlert } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { initializeAbpSession } from "../api/abpLogin";
import { clearApiSession } from "../util/ApiOptionsHelper";
import { getRequestedURL } from "../util/UrlHelper";
import { TranslationMessagesContext } from "../util/Translations";

interface ApiErrorProps {
    errorData: any;
    showRelogin?: boolean;
    showCancel?: boolean;
    showOk?: boolean;
    setShowLoading?: (loading: boolean) => void;    
    onOkOverride?: () => void | Promise<void>;
    onCancelOverride?: () => void | Promise<void>;
};

const ApiError: React.FC<ApiErrorProps> = (props) => {
    const { errorData, showRelogin, showCancel, showOk, setShowLoading, onOkOverride, onCancelOverride } = props;
    const [errorText, setErrorText] = useState("");
    const [pendingLogout, setPendingLogout] = useState(false);
    
    useEffect(() => {
        setErrorText(errorData);
    }, [errorData]);

    const onReLogin = async () => {
        setErrorText("");
        if (setShowLoading) setShowLoading(true);
        await clearApiSession();
        window.location.href = "/AuthCallback";
    };

    const onOk = async () => {
        if (onOkOverride) {
            await onOkOverride();
            return;
        }
        setErrorText("");
        if (setShowLoading) setShowLoading(true);

        try {
            const sessionResult = await initializeAbpSession(true);

            if (!sessionResult.validLogin) {
                setErrorText(sessionResult.message || "Session initialization failed. Please try again.");
                if (setShowLoading) setShowLoading(false);
                return;
            }

            if (globalThis.isLoginSuccessful) {
                const returnPath = await getRequestedURL();
                window.location.href = returnPath || "/Home";
            } else {
                setErrorText("Login was not completed successfully. Please try again.");
                if (setShowLoading) setShowLoading(false);
            }
        } catch (error) {
            console.error("onOk - initializeAbpSession error:", error);
            setErrorText("An unexpected error occurred. Please try again.");
            if (setShowLoading) setShowLoading(false);
        }
    };

    const onCancel = async () => {
        if (onCancelOverride) {
            await onCancelOverride();
            return;
        }
        setErrorText("");
        if (setShowLoading) setShowLoading(true);
        await clearApiSession();
        window.location.href = "/AuthCallback";
    }

    // When no explicit button props are provided and the message requires re-login,
    // redirect to login on OK instead of just dismissing.
    // Match all session-expired variants used across the codebase:
    //   "relogin", "re-login", "session has expired", and the translated SessionExpired text.
    const { translatedMessages } = useContext(TranslationMessagesContext);
    const sessionExpiredMsg = translatedMessages["SessionExpired"]?.MessageText || "";
    const errorStr = errorText?.toString() || "";
    const errorLower = errorStr.toLowerCase().trim();
    const requiresRelogin = !showOk && !showCancel && !showRelogin &&
        (errorLower.includes("relogin") ||
            errorLower.includes("re-login") ||
            (!globalThis.abpRevalidateSession && errorLower.includes("session has expired")) ||
            (!!sessionExpiredMsg && errorStr === sessionExpiredMsg));

    const onSessionExpiredOk = () => {
        globalThis.relogin = true;
        setPendingLogout(true);
        setErrorText("");
    };

    const alertButtons = [
        ...(showOk ? [{ text: "OK", handler: () => { onOk(); } }] : []),
        ...(showCancel ? [{ text: "Cancel", handler: () => { onCancel(); } }] : []),
        ...(showRelogin ? [{ text: "Retry Login", handler: () => { onReLogin(); } }] : []),
        ...(requiresRelogin ? [{ text: "OK", handler: () => { onSessionExpiredOk(); } }] : []),
    ];

    return (
        <IonAlert
            isOpen={!!errorText}
            message={errorText?.toString()}
            backdropDismiss={false}
            buttons={alertButtons.length > 0 ? alertButtons : ["OK"]}
            onDidDismiss={() => {
                setErrorText("");
                if (pendingLogout) {
                    setPendingLogout(false);
                    window.location.href = "/logout?relogin=true";
                }
            }}
        />
    );
};

export default ApiError;
